import React, { useState } from 'react';
import { MapGeometry } from '../../services/mapsApi';
import { NETWORK_LINKS } from '../../config/catalogue';

export interface RoadType {
  id: string;
  name: string;
  description: string;
}

interface RoadSelectComponentProps {
  selectedRoadType: string;
  onRoadTypeChange: (roadTypeId: string) => void;
  selectedRoads: MapGeometry[];
  onRoadRemove: (roadId: string) => void;
  onRoadsExport: () => void;
  onAddById?: (ids: string[]) => Promise<void>;
}

const roadTypes: RoadType[] = [
  {
    id: NETWORK_LINKS,
    name: 'Road network',
    description: 'The road links the traffic flows are modelled on (catalogue 1.0)'
  },
  {
    id: 'osm_links',
    name: 'OpenStreetMap Roads',
    description: 'Road network data from OpenStreetMap'
  },
  {
    id: 'statens_vegvesen',
    name: 'Statens Vegvesen Roads',
    description: 'Official Norwegian road network from Statens Vegvesen'
  }
];

// Road links are made on the catalogue's network, whose ids the flows use (catalogue 1.0); OSM links are no longer offered.
const availableRoadTypes = [NETWORK_LINKS];
const shownRoadTypes = roadTypes.filter((t) => t.id !== 'osm_links');

const RoadSelectComponent: React.FC<RoadSelectComponentProps> = ({
  selectedRoadType,
  onRoadTypeChange,
  selectedRoads,
  onRoadRemove,
  onRoadsExport,
  onAddById
}) => {
  const [idInput, setIdInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddById = async () => {
    if (!idInput.trim() || !selectedRoadType || !onAddById) return;

    setIsAdding(true);
    try {
      // Split by comma, semicolon, or whitespace and filter empty strings
      const ids = idInput
        .split(/[,;\s]+/)
        .map(id => id.trim())
        .filter(id => id.length > 0);

      if (ids.length > 0) {
        await onAddById(ids);
        setIdInput('');
      }
    } catch (error) {
      console.error('Failed to add roads by ID:', error);
      alert(`Failed to add roads: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddById();
    }
  };

  return (
    <div className="w-80 bg-surface border-l border-paper-300 flex flex-col h-full">
      <div className="p-4 border-b border-paper-300">
        <h2 className="text-lg font-semibold text-ink-soft mb-2">
          Road Network Selection
        </h2>
        <p className="text-sm text-ink-muted">
          Choose the road network data source to display on the map
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3">
          {shownRoadTypes.map((roadType) => {
            const isAvailable = availableRoadTypes.includes(roadType.id);
            return (
              <div key={roadType.id} className={`border border-paper-300 rounded-lg p-3 ${!isAvailable ? 'opacity-50 bg-paper-100' : ''}`}>
                <label className={`flex items-start space-x-3 ${isAvailable ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                  <input
                    type="radio"
                    name="roadType"
                    value={roadType.id}
                    checked={selectedRoadType === roadType.id}
                    onChange={(e) => onRoadTypeChange(e.target.value)}
                    disabled={!isAvailable}
                    className={`mt-1 h-4 w-4 text-brand-500 focus:ring-brand-500 border-paper-400 ${!isAvailable ? 'cursor-not-allowed' : ''}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${isAvailable ? 'text-ink' : 'text-ink-faint'}`}>
                      {roadType.name}
                      {!isAvailable && <span className="ml-2 text-xs text-ink-faint">(Coming Soon)</span>}
                    </div>
                    <div className={`text-sm mt-1 ${isAvailable ? 'text-ink-subtle' : 'text-ink-faint'}`}>
                      {roadType.description}
                    </div>
                  </div>
                </label>
              </div>
            );
          })}
        </div>

        {/* Selected Roads Section */}
        <div className="mt-6">
          <div className="border border-paper-300 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-ink">Selected Roads</h3>
              {selectedRoads.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ok-soft text-ok-ink">
                  {selectedRoads.length}
                </span>
              )}
            </div>

            {/* Add by ID input - only show when road type is selected */}
            {selectedRoadType && onAddById && (
              <div className="mb-3">
                <label className="block text-xs text-ink-muted mb-1">
                  Add by Road ID(s)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={idInput}
                    onChange={(e) => setIdInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter road ID(s), comma separated"
                    className="flex-1 px-2 py-1 text-xs bg-surface border border-paper-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                    disabled={isAdding}
                  />
                  <button
                    onClick={handleAddById}
                    disabled={!idInput.trim() || isAdding}
                    className="px-3 py-1 text-xs bg-brand-500 text-on-brand rounded hover:bg-brand-600 disabled:bg-paper-400 disabled:cursor-not-allowed"
                  >
                    {isAdding ? '...' : 'Add'}
                  </button>
                </div>
              </div>
            )}

            {selectedRoads.length === 0 ? (
              <div className="text-xs text-ink-subtle">
                No roads selected. {selectedRoadType ? 'Click on map roads or add by ID above.' : 'Click on map roads to select them.'}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="max-h-48 overflow-y-auto">
                  {selectedRoads.map((road) => (
                    <div
                      key={road.geo_id}
                      className="flex items-center justify-between bg-paper-100 p-2 rounded"
                    >
                      <span className="text-xs font-mono text-ink-soft">
                        {road.geo_id}
                      </span>
                      <button
                        onClick={() => onRoadRemove(road.geo_id)}
                        className="text-bad-ink hover:bg-bad-soft rounded px-1"
                        title="Remove road"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={onRoadsExport}
                  className="w-full px-3 py-2 text-sm bg-brand-500 text-on-brand rounded hover:bg-brand-600"
                >
                  Save Roads ({selectedRoads.length})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-paper-300 bg-paper-100">
        <div className="text-xs text-ink-subtle">
          Selected: <span className="font-medium text-ink-soft">
            {roadTypes.find(t => t.id === selectedRoadType)?.name || 'None'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RoadSelectComponent;
